import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Mail, Lock, User, Building, ShieldCheck } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const Register = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('form');
  const [otp, setOtp] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const { register, verifyOtp, resendOtp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref') || localStorage.getItem('ref') || '';
  const inviteTeam = searchParams.get('team') || '';
  const resentOnMount = useRef(false);

  useEffect(() => {
    const inviteEmail = searchParams.get('email');
    if (inviteEmail) setEmail(inviteEmail);
    const inviteName = searchParams.get('name');
    if (inviteName) setFullName(inviteName);
  }, []);

  useEffect(() => {
    const verifyEmail = location.state?.verifyEmail;
    if (verifyEmail && !resentOnMount.current) {
      resentOnMount.current = true;
      setEmail(verifyEmail);
      setStep('otp');
      resendOtp(verifyEmail);
      setResendCooldown(60);
    }
  }, [location.state, resendOtp]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await register(email, password, fullName, businessName, refCode);

    if (result.success && result.requiresVerification) {
      localStorage.removeItem('ref');
      setStep('otp');
      setResendCooldown(60);
      toast({
        title: 'Check your email',
        description: `We sent a 6-digit verification code to ${email}.`,
      });
    } else if (result.success) {
      localStorage.removeItem('ref');
      toast({
        title: 'Success!',
        description: 'Account created successfully. Redirecting to dashboard...',
      });
      setTimeout(() => navigate('/dashboard'), 1000);
    } else {
      toast({
        title: 'Registration Failed',
        description: result.error,
        variant: 'destructive'
      });
    }

    setLoading(false);
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) return;
    setLoading(true);
    const result = await verifyOtp(email, otp);
    if (result.success) {
      toast({
        title: 'Email verified!',
        description: 'Your account is ready. Redirecting to dashboard...',
      });
      setTimeout(() => navigate('/dashboard'), 800);
    } else {
      toast({
        title: 'Verification Failed',
        description: result.error,
        variant: 'destructive'
      });
    }
    setLoading(false);
  };

  const handleResend = async () => {
    const result = await resendOtp(email);
    if (result.success) {
      setResendCooldown(60);
      toast({ title: 'Code sent', description: `A new code was sent to ${email}.` });
    } else {
      toast({ title: 'Could not resend', description: result.error, variant: 'destructive' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0d1f35] to-[#0a1628] flex items-center justify-center px-6 py-12">
      <Card className="w-full max-w-md bg-gradient-to-br from-teal-900/20 to-purple-900/20 border-white/10">
        <CardHeader className="space-y-4">
          <div className="flex justify-center">
            <Link to="/" className="flex flex-col items-center gap-2 hover:opacity-80 transition-opacity" data-testid="register-logo">
              <img src="/logo-mark.png" alt="Miadesk" className="h-12 w-auto" />
              <span className="text-white/60 text-sm font-medium">Miadesk</span>
            </Link>
          </div>
          {step === 'form' ? (
            <>
              <CardTitle className="text-2xl text-center text-white">Create your account</CardTitle>
              <CardDescription className="text-center text-gray-400">
                Start your 7-day free trial. No credit card required.
              </CardDescription>
              {inviteTeam && (
                <div className="mt-3 rounded-lg bg-purple-500/10 border border-purple-500/30 px-4 py-3 text-center" data-testid="invite-banner">
                  <p className="text-sm text-purple-300">
                    You've been invited to join <span className="font-semibold text-white">{inviteTeam}</span> — create your password below and you'll join the team automatically.
                  </p>
                </div>
              )}
            </>
          ) : (
            <>
              <CardTitle className="text-2xl text-center text-white flex items-center justify-center gap-2">
                <ShieldCheck className="w-6 h-6 text-purple-400" /> Verify your email
              </CardTitle>
              <CardDescription className="text-center text-gray-400">
                Enter the 6-digit code we sent to <span className="text-white">{email}</span>
              </CardDescription>
            </>
          )}
        </CardHeader>
        <CardContent>
          {step === 'form' ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="fullName" className="text-gray-200">
                Full Name
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="fullName"
                  type="text"
                  placeholder="John Doe"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  className="pl-10 bg-black/30 border-white/10 text-white placeholder:text-gray-500"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="businessName" className="text-gray-200">
                Business Name
              </Label>
              <div className="relative">
                <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="businessName"
                  type="text"
                  placeholder="Your Business LLC"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  className="pl-10 bg-black/30 border-white/10 text-white placeholder:text-gray-500"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-200">
                Email
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="pl-10 bg-black/30 border-white/10 text-white placeholder:text-gray-500"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-200">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="pl-10 bg-black/30 border-white/10 text-white placeholder:text-gray-500"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600 text-white"
              data-testid="register-submit-btn"
            >
              {loading ? 'Creating account...' : 'Create account'}
            </Button>

            <p className="text-center text-sm text-gray-400">
              Already have an account?{' '}
              <Link to="/login" className="text-purple-400 hover:text-purple-300">
                Sign in
              </Link>
            </p>
          </form>
          ) : (
          <form onSubmit={handleVerify} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="otp" className="text-gray-200">Verification code</Label>
              <Input
                id="otp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="••••••"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                autoFocus
                className="bg-black/30 border-white/10 text-white placeholder:text-gray-500 text-center text-2xl tracking-[0.5em] font-mono h-14"
                data-testid="otp-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading || otp.length !== 6}
              className="w-full bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600 text-white"
              data-testid="verify-otp-btn"
            >
              {loading ? 'Verifying...' : 'Verify & Continue'}
            </Button>

            <div className="text-center text-sm text-gray-400 space-y-2">
              <p>
                Didn't receive the code?{' '}
                {resendCooldown > 0 ? (
                  <span className="text-gray-500" data-testid="resend-cooldown">Resend in {resendCooldown}s</span>
                ) : (
                  <button type="button" onClick={handleResend} className="text-purple-400 hover:text-purple-300" data-testid="resend-otp-btn">
                    Resend code
                  </button>
                )}
              </p>
              <p>
                Wrong email?{' '}
                <button type="button" onClick={() => { setStep('form'); setOtp(''); }} className="text-purple-400 hover:text-purple-300" data-testid="back-to-form-btn">
                  Go back
                </button>
              </p>
            </div>
          </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Register;
